import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

// Mock data for available time slots
const mockTimeSlots = [
  {
    date: '2024-01-20',
    dayName: '土',
    timeSlots: ['10:00-10:40', '14:00-14:40', '16:00-16:40']
  },
  {
    date: '2024-01-22',
    dayName: '月', 
    timeSlots: ['10:00-10:40', '11:00-11:40', '13:00-13:40', '15:00-15:40']
  },
  {
    date: '2024-01-23',
    dayName: '火',
    timeSlots: ['10:00-10:40', '14:00-14:40']
  },
  {
    date: '2024-01-24',
    dayName: '水',
    timeSlots: ['11:00-11:40', '13:00-13:40', '15:00-15:40']
  },
  {
    date: '2024-01-25',
    dayName: '木',
    timeSlots: ['10:00-10:40', '14:00-14:40', '16:00-16:40']
  },
  {
    date: '2024-01-26',
    dayName: '金',
    timeSlots: ['10:00-10:40', '11:00-11:40', '15:00-15:40']
  },
  {
    date: '2024-01-29',
    dayName: '月',
    timeSlots: ['10:00-10:40', '13:00-13:40', '14:00-14:40']
  }
];

export default function BookingGuidePage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  // Booking form data
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    concerns: '',
    message: '',
    guardianName: '',
    guardianPhone: '',
    guardianEmail: '',
    guardianRelation: '',
    isMinor: false,
    parentalConsent: false,
    parentEmail: '',
    termsAgreed: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Load guardian info from localStorage if exists
  useEffect(() => {
    const guardianInfo = localStorage.getItem('guardianInfo');
    if (guardianInfo) {
      try {
        const parsed = JSON.parse(guardianInfo);
        setFormData(prev => ({
          ...prev,
          guardianName: parsed.guardianName || '',
          guardianPhone: parsed.guardianPhone || '',
          guardianEmail: parsed.guardianEmail || '',
          guardianRelation: parsed.guardianRelation || ''
        }));
      } catch (e) {
        console.error('Failed to parse guardian info');
      }
    }
  }, []);

  // Age change handler to detect if user is minor
  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const age = parseInt(e.target.value);
    const isMinor = age < 18;
    
    setFormData(prev => ({ 
      ...prev, 
      age: e.target.value,
      isMinor,
      parentalConsent: isMinor ? false : true,
      parentEmail: isMinor ? prev.parentEmail : ''
    }));
  };

  const handleBookingClick = () => {
    navigate('/', { replace: false });
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleTimeSelection = (date: string, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
    
    // Auto-fill form data
    const selectedDateFormatted = new Date(date).toISOString().split('T')[0];
    const timeMapping: { [key: string]: string } = {
      '10:00-10:40': 'morning',
      '11:00-11:40': 'morning',
      '13:00-13:40': 'afternoon',
      '14:00-14:40': 'afternoon',
      '15:00-15:40': 'afternoon',
      '16:00-16:40': 'afternoon'
    };
    
    setFormData(prev => ({
      ...prev,
      preferredDate: selectedDateFormatted,
      preferredTime: timeMapping[time] || 'morning'
    }));
  };

  const handleShowForm = () => {
    if (selectedDate && selectedTime) {
      setShowBookingForm(true);
      setTimeout(() => {
        document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation for terms agreement
    if (!formData.termsAgreed) {
      alert('注意事項・免責事項への同意が必要です。');
      setIsSubmitting(false);
      return;
    }

    // Validation for minors
    if (formData.isMinor) {
      if (!formData.parentalConsent) {
        alert('未成年の方は保護者の同意が必要です。');
        setIsSubmitting(false);
        return;
      }
      if (!formData.parentEmail.trim()) {
        alert('未成年の方は保護者のメールアドレスの入力が必要です。');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Submit to Readdy Form
      const formBody = new URLSearchParams();
      
      // Add selected time info to form data
      const selectedSlot = mockTimeSlots.find(slot => slot.date === selectedDate);
      formBody.append('selectedDateTime', `${new Date(selectedDate).getMonth() + 1}月${new Date(selectedDate).getDate()}日(${selectedSlot?.dayName}) ${selectedTime}`);
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== false) {
          formBody.append(key, value.toString());
        }
      });

      const formResponse = await fetch('https://readdy.ai/api/form/d5i64drihlvellpk58qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (!formResponse.ok) {
        throw new Error('Form submission failed');
      }

      // Send confirmation email via Supabase Edge Function
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const emailResponse = await fetch(`${supabaseUrl}/functions/v1/send-booking-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          customerEmail: formData.email,
          customerName: formData.name,
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          concerns: formData.concerns,
          isMinor: formData.isMinor,
          parentEmail: formData.isMinor ? formData.parentEmail : null
        })
      });

      if (!emailResponse.ok) {
        console.error('Email sending failed, but form was submitted');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        age: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        concerns: '',
        message: '',
        guardianName: '',
        guardianPhone: '',
        guardianEmail: '',
        guardianRelation: '',
        isMinor: false,
        parentalConsent: false,
        parentEmail: '',
        termsAgreed: false
      });
      setSelectedDate('');
      setSelectedTime('');
      localStorage.removeItem('guardianInfo');
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleConsentClick = () => {
    navigate('/consent');
  };

  const hasGuardianInfo = formData.guardianName && formData.guardianPhone && formData.guardianEmail;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Klee One', cursive" }}>
            こころケア
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">ホーム</Link>
            <Link to="/profile" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">セラピスト紹介</Link>
            <Link to="/payment" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">お支払い</Link>
            <Link to="/booking-guide" className="text-[#FF9B8A] hover:text-[#FF8A77] transition-colors text-sm font-medium">予約方法</Link>
            <Link to="/terms" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">注意事項</Link>
            <Link to="/faq" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">よくある質問</Link>
          </div>

          <button 
            onClick={handleBookingClick}
            className="bg-[#FF9B8A] hover:bg-[#FF8A77] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            今すぐ予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5F3] via-white to-[#F0F9FF]"></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-[#FF9B8A]/10 rounded-full mb-6">
            <span className="text-[#FF9B8A] text-sm font-medium">簡単3ステップ</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
            予約方法
          </h1>
          
          <p className="text-lg text-gray-600">
            お気軽にご利用いただけるよう、シンプルな予約システムをご用意しています
          </p>
        </div>
      </section>

      {/* Booking Process */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
              予約の流れ
            </h2>
            <p className="text-gray-600 text-lg">
              わずか3ステップで簡単にご予約いただけます
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF9B8A] to-[#FFD93D] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Klee One', cursive" }}>
                時間を選択
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                リアルタイムカレンダーから空いている時間を選択してください。
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#A8D5E2] to-[#B4A5D5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Klee One', cursive" }}>
                フォームを送信
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                選択した時間で予約フォームを送信してください。5分程度で完了します。
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#6B4E3D] to-[#8B6F5E] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Klee One', cursive" }}>
                確認連絡
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                24時間以内にメールで確認連絡をいたします。Google Meetのリンクをお送りします。
              </p>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-gradient-to-br from-[#FFFBF0] to-white rounded-3xl p-8 border-2 border-[#FFD93D]/30 mb-16">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#FFD93D] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-white text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
                  予約のタイミングについて
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p className="leading-relaxed">
                    <strong>予約フォームをお送りいただいた後で日程調整をセラピストとさせていただきます。</strong>
                  </p>
                  <p className="leading-relaxed">
                    他の事業も行っているため、<strong className="text-[#FFD93D]">予約が比較的取りやすい時間帯は平日の10時から16時</strong>です。
                  </p>
                  <p className="text-sm text-gray-600">
                    ※ 土日祝日や夜間のお時間も対応可能な場合がございますので、ご希望がございましたらお気軽にお申し付けください。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#FFD93D]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-[#FF9B8A] text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">セッション時間</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  1回のセッションは<strong className="text-[#FF9B8A]">40分間</strong>です。
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF9B8A] mt-1">•</span>
                    <span>開始時刻の5分前には準備をお願いします</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF9B8A] mt-1">•</span>
                    <span>時間内で自由にお話しください</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF9B8A] mt-1">•</span>
                    <span>終了5分前にお知らせします</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real-time Calendar */}
      <section className="py-16 px-6 bg-[#FFF8F3]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
              空き時間を選択
            </h2>
            <p className="text-gray-600 text-lg mb-2">
              現在予約可能な時間帯から選択してください
            </p>
            <p className="text-sm text-gray-500">
              ※ 表示されている時間は空き状況の目安です。正確な予約確定は日程調整の際に行います。
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#FF9B8A]/20">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-8 h-8 bg-[#FF9B8A] rounded-full flex items-center justify-center">
                  <i className="ri-calendar-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">今週〜来週の空き状況</h3>
              </div>
              
              <div className="grid gap-4">
                {mockTimeSlots.map((dayData, index) => (
                  <div 
                    key={index}
                    className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-[#FF9B8A]/30 transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="text-sm text-gray-600">
                          {new Date(dayData.date).getMonth() + 1}月{new Date(dayData.date).getDate()}日
                        </div>
                        <div className="text-xl font-bold text-gray-900">
                          ({dayData.dayName})
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-3">
                          {dayData.timeSlots.map((timeSlot, timeIndex) => (
                            <button
                              key={timeIndex}
                              onClick={() => handleTimeSelection(dayData.date, timeSlot)}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                                selectedDate === dayData.date && selectedTime === timeSlot
                                  ? 'bg-[#FF9B8A] text-white shadow-lg'
                                  : 'bg-white border-2 border-[#FF9B8A]/30 text-[#FF9B8A] hover:bg-[#FF9B8A]/10'
                              }`}
                            >
                              {timeSlot}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          空きあり
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedDate && selectedTime && (
              <div className="bg-gradient-to-br from-[#FF9B8A]/10 to-[#FFD93D]/10 rounded-2xl p-6 border-2 border-[#FF9B8A]/30">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-8 h-8 bg-[#FF9B8A] rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">選択中の時間</h4>
                </div>
                
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-gray-700">
                      <span className="font-medium">
                        {new Date(selectedDate).getMonth() + 1}月{new Date(selectedDate).getDate()}日
                        ({mockTimeSlots.find(slot => slot.date === selectedDate)?.dayName})
                      </span>
                      <span className="mx-2">•</span>
                      <span className="font-medium">{selectedTime}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      ※ この時間帯をご希望として予約フォームに自動入力されます
                    </p>
                  </div>
                  
                  <button
                    onClick={handleShowForm}
                    className="bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    この時間で予約する →
                  </button>
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-gray-500 text-lg w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  <p className="mb-2">
                    <strong>カレンダー更新について：</strong>
                    空き状況は随時更新されます。表示されている時間でも、先約が入った場合は調整が必要になることがございます。
                  </p>
                  <p>
                    正確な予約確定は、フォーム送信後のメールでの日程調整にて行わせていただきます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      {showBookingForm && (
        <section id="booking-form" className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                予約フォーム
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                選択された時間でご予約を進めます
              </p>
              <div className="inline-block bg-gradient-to-br from-[#FF9B8A]/10 to-[#FFD93D]/10 rounded-2xl p-4 border-2 border-[#FF9B8A]/30">
                <p className="text-gray-800 font-medium">
                  選択時間: <span className="text-[#FF9B8A] font-bold">
                    {new Date(selectedDate).getMonth() + 1}月{new Date(selectedDate).getDate()}日
                    ({mockTimeSlots.find(slot => slot.date === selectedDate)?.dayName}) {selectedTime}
                  </span>
                </p>
              </div>
            </div>

            {/* Consent Notice */}
            <div className="mb-12 bg-gradient-to-br from-[#FF9B8A]/10 to-[#FFD93D]/10 rounded-3xl p-8 border-2 border-[#FF9B8A]/30">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#FF9B8A] to-[#FFD93D] rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-file-list-3-line text-white text-3xl w-8 h-8 flex items-center justify-center"></i>
                  </div>
                </div>
                
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Klee One', cursive" }}>
                    予約前に必ずご確認ください
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-1">
                    本サービスは<strong className="text-[#FF9B8A]">医療行為ではありません</strong>。注意事項への同意が必要です。
                  </p>
                  <p className="text-gray-600 text-sm">
                    18歳未満の方は保護者の同意が必須です。
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <button
                    onClick={handleConsentClick}
                    className="bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white px-8 py-4 rounded-full text-base font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-checkbox-circle-line text-xl w-5 h-5 flex items-center justify-center"></i>
                    同意確認へ
                  </button>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSubmit} data-readdy-form className="bg-[#FFF8F3] rounded-3xl p-8 md:p-12 shadow-lg border-2 border-[#FF9B8A]/30">
              {/* Guardian Info Display (if exists) */}
              {hasGuardianInfo && (
                <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-parent-line text-yellow-700 text-lg w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-yellow-900 mb-1">保護者情報が登録されています</h4>
                      <p className="text-sm text-yellow-800">
                        保護者名: <strong>{formData.guardianName}</strong> ({formData.guardianRelation})<br />
                        メールアドレス: <strong>{formData.guardianEmail}</strong><br />
                        電話番号: <strong>{formData.guardianPhone}</strong>
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleConsentClick}
                    className="text-sm text-yellow-700 hover:text-yellow-900 font-medium cursor-pointer"
                  >
                    変更する →
                  </button>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年齢 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleAgeChange}
                    required
                    min="1"
                    max="100"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                    placeholder="16"
                  />
                </div>
              </div>

              {/* Minor Consent Section */}
              {formData.isMinor && (
                <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-parent-line text-yellow-700 text-xl w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-yellow-900 mb-2">未成年者の保護者同意</h4>
                      <p className="text-sm text-yellow-800 mb-4">
                        18歳未満の方がサービスをご利用される場合、保護者の同意が必要です。
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        保護者のメールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="parentEmail"
                        value={formData.parentEmail}
                        onChange={handleChange}
                        required={formData.isMinor}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FFD93D] text-sm"
                        placeholder="parent@example.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        ※ 保護者の方に確認メールをお送りします
                      </p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative flex-shrink-0 mt-1">
                        <input
                          type="checkbox"
                          name="parentalConsent"
                          checked={formData.parentalConsent}
                          onChange={handleChange}
                          required={formData.isMinor}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          formData.parentalConsent 
                            ? 'bg-[#FFD93D] border-[#FFD93D]' 
                            : 'border-gray-300 group-hover:border-[#FFD93D]'
                        }`}>
                          {formData.parentalConsent && (
                            <i className="ri-check-line text-white text-sm w-3 h-3 flex items-center justify-center"></i>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          保護者の同意を得ています <span className="text-red-500">*</span>
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          保護者の方がこのサービスの利用に同意していることを確認してください
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  相談したい内容 <span className="text-red-500">*</span>
                </label>
                <select
                  name="concerns"
                  value={formData.concerns}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="love">恋愛の悩み</option>
                  <option value="body">体の変化・悩み</option>
                  <option value="eating">食べ方の悩み</option>
                  <option value="ocd">一つの考えに縛られる</option>
                  <option value="parents">親との関係</option>
                  <option value="daily">日常のモヤモヤ</option>
                  <option value="other">その他</option>
                </select>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メッセージ（任意）
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF9B8A] text-sm resize-none"
                  placeholder="事前に伝えておきたいことがあれば、お気軽にお書きください（500文字まで）"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              {/* Terms Agreement Section */}
              <div className="mb-8 bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-file-list-3-line text-gray-700 text-xl w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">注意事項・免責事項への同意</h4>
                    <p className="text-sm text-gray-700 mb-4">
                      ご予約の前に、必ず注意事項をご確認いただき、同意してください。
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => navigate('/terms')}
                    className="text-[#FF9B8A] hover:text-[#FF8A77] text-sm font-medium flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-external-link-line text-base w-4 h-4 flex items-center justify-center"></i>
                    注意事項・免責事項を確認する
                  </button>
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      name="termsAgreed"
                      checked={formData.termsAgreed}
                      onChange={handleChange}
                      required
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      formData.termsAgreed 
                        ? 'bg-[#FF9B8A] border-[#FF9B8A]' 
                        : 'border-gray-300 group-hover:border-[#FF9B8A]'
                    }`}>
                      {formData.termsAgreed && (
                        <i className="ri-check-line text-white text-sm w-3 h-3 flex items-center justify-center"></i>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      注意事項・免責事項を確認し、同意します <span className="text-red-500">*</span>
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      本サービスが医療行為ではないこと、および利用は自己責任であることを理解しました
                    </p>
                  </div>
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
                  予約リクエストを受け付けました。確認のメールをお送りしますので、しばらくお待ちください。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !selectedDate || !selectedTime}
                className="w-full bg-[#6B4E3D] hover:bg-[#5A3F2E] text-white px-8 py-4 rounded-full text-base font-medium transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '予約リクエストを送る →'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                ※ 予約確定後、Google Meetのリンクをメールでお送りします
              </p>
            </form>
          </div>
        </section>
      )}

      {/* Alternative CTA Section */}
      {!showBookingForm && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Klee One', cursive" }}>
              まずは時間を選択してください
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              上記のリアルタイムカレンダーから空いている時間を選択すると、予約フォームが表示されます
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/consent"
                className="inline-block bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white px-10 py-4 rounded-full text-base font-medium transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                同意確認から始める
              </Link>
              
              <button
                onClick={handleBookingClick}
                className="inline-block bg-white border-2 border-[#FF9B8A] text-[#FF9B8A] hover:bg-[#FF9B8A] hover:text-white px-10 py-4 rounded-full text-base font-medium transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                ホームページの予約フォームへ
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mt-6">
              ご不明な点がございましたら、<Link to="/faq" className="text-[#FF9B8A] hover:underline cursor-pointer">よくある質問</Link>をご覧ください
            </p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
